/* ============================================================
   WEAR YOUR PRIDE — theme.js
   Vanilla JS, minimal dependencies, progressive enhancement
   ============================================================ */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  /* ---------- Fetch config ---------- */
  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
    };
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    let el = $('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.textContent = message;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3000);
  }

  /* ---------- Sticky header scroll state ---------- */
  function initHeader() {
    const header = $('#SiteHeader');
    if (!header) return;
    const transparent = header.classList.contains('site-header--transparent');

    const update = () => {
      if (window.scrollY > 24) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    };
    update();
    on(window, 'scroll', update, { passive: true });

    /* Search bar toggle */
    const searchToggle = $('[data-search-toggle]', header);
    const searchBar = $('[data-search-bar]', header);
    const searchClose = $('[data-search-close]', header);
    const searchInput = $('[data-search-input]', header);

    const openSearch = () => {
      if (!searchBar) return;
      searchBar.hidden = false;
      setTimeout(() => searchInput && searchInput.focus(), 50);
    };
    const closeSearch = () => {
      if (!searchBar) return;
      searchBar.hidden = true;
    };
    on(searchToggle, 'click', openSearch);
    on(searchClose, 'click', closeSearch);
    on(document, 'keydown', (e) => { if (e.key === 'Escape') closeSearch(); });

    /* Dropdown hover focus management */
    $$('.has-dropdown', header).forEach((item) => {
      const link = $('.site-header__menu-link', item);
      on(link, 'keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const expanded = link.getAttribute('aria-expanded') === 'true';
          link.setAttribute('aria-expanded', String(!expanded));
        }
      });
    });
  }

  /* ---------- Mobile menu ---------- */
  function initMobileMenu() {
    const menu = $('#MobileMenu');
    const overlay = $('[data-menu-overlay]');
    const toggle = $('[data-menu-toggle]');
    const close = $('[data-menu-close]');
    if (!menu || !toggle) return;

    const open = () => {
      menu.hidden = false;
      overlay.hidden = false;
      requestAnimationFrame(() => {
        menu.classList.add('is-open');
        overlay.classList.add('is-open');
      });
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };
    const closeMenu = () => {
      menu.classList.remove('is-open');
      overlay.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      setTimeout(() => { menu.hidden = true; overlay.hidden = true; }, 400);
    };
    on(toggle, 'click', open);
    on(close, 'click', closeMenu);
    on(overlay, 'click', closeMenu);
    on(document, 'keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    const els = $$('[data-animate]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Cart drawer ---------- */
  function initCart() {
    const drawer = $('[data-cart-drawer]');
    const overlay = $('[data-cart-drawer-overlay]');
    if (!drawer) return;

    const open = () => {
      drawer.hidden = false;
      overlay.hidden = false;
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        overlay.classList.add('is-open');
      });
      document.body.classList.add('cart-drawer-open');
    };
    const close = () => {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.classList.remove('cart-drawer-open');
      setTimeout(() => { drawer.hidden = true; overlay.hidden = true; }, 400);
    };

    on($('[data-cart-link]'), 'click', (e) => {
      if (window.routes) {
        e.preventDefault();
        open();
      }
    });
    on($('[data-cart-drawer-close]', drawer), 'click', close);
    on(overlay, 'click', close);
    on(document, 'keydown', (e) => { if (e.key === 'Escape') close(); });

    /* Render cart HTML from JSON */
    function renderCart(cart) {
      const itemsEl = $('[data-cart-items]', drawer);
      const subtotalEl = $('[data-cart-subtotal]', drawer);
      const countEls = $$('[data-cart-count]');
      const emptyEl = $('[data-cart-empty]', drawer);
      const formEl = $('[data-cart-form]', drawer);
      const footerEl = $('[data-cart-footer]', drawer);

      countEls.forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });

      if (cart.item_count === 0) {
        if (emptyEl) emptyEl.hidden = false;
        if (formEl) formEl.hidden = true;
        if (footerEl) footerEl.hidden = true;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      if (formEl) formEl.hidden = false;
      if (footerEl) footerEl.hidden = false;

      if (itemsEl) {
        const money = (cents) => {
          const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: cart.currency
          });
          return fmt.format(cents / 100);
        };
        itemsEl.innerHTML = cart.items.map((item, i) => `
          <li class="cart-item" data-cart-item data-line="${i + 1}" data-key="${item.key}">
            <div class="cart-item__media">
              ${item.image ? `<img src="${item.image}&width=160" alt="${escapeHtml(item.title)}" width="80" height="100" loading="lazy" class="cart-item__image">` : ''}
            </div>
            <div class="cart-item__info">
              <a href="${item.url}" class="cart-item__title">${escapeHtml(item.product_title)}</a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>` : ''}
              <p class="cart-item__price">${money(item.final_line_price)}</p>
              <div class="cart-item__quantity" data-quantity>
                <button type="button" class="qty-btn" data-qty-decrement aria-label="Decrease">&minus;</button>
                <input type="number" class="qty-input" value="${item.quantity}" min="0" data-qty-input>
                <button type="button" class="qty-btn" data-qty-increment aria-label="Increase">+</button>
              </div>
            </div>
            <button type="button" class="cart-item__remove icon-button" data-item-remove aria-label="Remove">
              <svg class="icon icon-close" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
            </button>
          </li>
        `).join('');
      }
      if (subtotalEl) {
        const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cart.currency });
        subtotalEl.textContent = fmt.format(cart.total_price / 100);
      }
      bindCartItemEvents();
    }

    function bindCartItemEvents() {
      $$('[data-cart-item]', drawer).forEach((item) => {
        const line = item.dataset.line;
        const key = item.dataset.key;
        const input = $('[data-qty-input]', item);
        const decBtn = $('[data-qty-decrement]', item);
        const incBtn = $('[data-qty-increment]', item);
        const removeBtn = $('[data-item-remove]', item);

        const changeQty = (qty) => updateLine(key, qty);
        on(decBtn, 'click', () => changeQty(Math.max(0, parseInt(input.value, 10) - 1)));
        on(incBtn, 'click', () => changeQty(parseInt(input.value, 10) + 1));
        on(input, 'change', () => changeQty(Math.max(0, parseInt(input.value, 10) || 0)));
        on(removeBtn, 'click', () => changeQty(0));
      });
    }

    async function updateLine(key, quantity) {
      try {
        const res = await fetch(window.routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await res.json();
        renderCart(cart);
      } catch (err) {
        toast('Could not update cart. Please try again.');
      }
    }

    async function addToCart(form) {
      const formData = new FormData(form);
      const id = formData.get('id');
      const quantity = formData.get('quantity') || 1;
      try {
        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: parseInt(quantity, 10) }] })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Add failed');
        const cartRes = await fetch(window.routes.cart_url + '.js', { headers: { 'Accept': 'application/json' } });
        const cart = await cartRes.json();
        renderCart(cart);
        open();
        toast('Added to cart');
      } catch (err) {
        toast(err.message || 'Could not add to cart');
      }
    }

    /* Quick add forms */
    $$('[data-product-form-quick]').forEach((form) => {
      on(form, 'submit', (e) => {
        e.preventDefault();
        addToCart(form);
      });
    });
    bindCartItemEvents();

    /* Expose for product page */
    window.WYP = window.WYP || {};
    window.WYP.addToCart = addToCart;
    window.WYP.openCart = open;
  }

  /* ---------- Reviews carousel ---------- */
  function initReviews() {
    const root = $('[data-reviews-carousel]');
    if (!root) return;
    const track = $('[data-reviews-track]', root);
    const prev = $('[data-reviews-prev]', root);
    const next = $('[data-reviews-next]', root);
    const dotsWrap = $('[data-reviews-dots]', root);
    if (!track) return;

    const cards = $$('.review-card', track);
    let index = 0;

    function cardsPerView() {
      const w = window.innerWidth;
      if (w >= 1100) return 3;
      if (w >= 768) return 2;
      return 1;
    }

    function maxIndex() { return Math.max(0, cards.length - cardsPerView()); }

    function buildDots() {
      if (!dotsWrap) return;
      const total = maxIndex() + 1;
      dotsWrap.innerHTML = '';
      for (let i = 0; i < total; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'reviews__dot' + (i === index ? ' is-active' : '');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(dot);
      }
    }

    function goTo(i) {
      index = Math.min(Math.max(0, i), maxIndex());
      const card = cards[index];
      if (card) track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      $$('.reviews__dot', dotsWrap).forEach((d, i2) => d.classList.toggle('is-active', i2 === index));
    }

    on(prev, 'click', () => goTo(index - 1));
    on(next, 'click', () => goTo(index + 1));

    let scrollTimer;
    on(track, 'scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const cardWidth = cards[0] ? cards[0].offsetWidth + 24 : 0;
        if (cardWidth) index = Math.round(track.scrollLeft / cardWidth);
        $$('.reviews__dot', dotsWrap).forEach((d, i2) => d.classList.toggle('is-active', i2 === index));
      }, 100);
    }, { passive: true });

    buildDots();
    on(window, 'resize', () => { index = Math.min(index, maxIndex()); buildDots(); });
  }

  /* ---------- Wishlist ---------- */
  function initWishlist() {
    const storageKey = 'wyp_wishlist';
    const get = () => { try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; } };
    const set = (arr) => localStorage.setItem(storageKey, JSON.stringify(arr));
    $$('[data-wishlist]').forEach((btn) => {
      const id = btn.dataset.productId;
      if (!id) return;
      if (get().includes(id)) btn.classList.add('is-active');
      on(btn, 'click', () => {
        const list = get();
        const i = list.indexOf(id);
        if (i > -1) { list.splice(i, 1); btn.classList.remove('is-active'); toast('Removed from wishlist'); }
        else { list.push(id); btn.classList.add('is-active'); toast('Saved to wishlist'); }
        set(list);
      });
    });
  }

  /* ---------- Product page gallery ---------- */
  function initProductGallery() {
    const main = $('.product__main-image img');
    const thumbs = $$('.product__thumb');
    if (!main || !thumbs.length) return;
    thumbs.forEach((thumb) => {
      on(thumb, 'click', () => {
        const img = $('img', thumb);
        if (!img) return;
        main.src = img.dataset.full || img.src.replace('&width=160', '&width=1200');
        main.alt = img.alt;
        thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  /* ---------- Helpers ---------- */
  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  /* ---------- Boot ---------- */
  function init() {
    initHeader();
    initMobileMenu();
    initReveal();
    initCart();
    initReviews();
    initWishlist();
    initProductGallery();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
